import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import styles from "./Profile.module.css";
import { useTranslation } from "react-i18next";

const BASE_URL = "https://admin.osleague.online/api";

export default function UserProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth.token);

  const [history, setHistory] = useState([]);
  const [userName, setUserName] = useState("");
  const [userImage, setUserImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getImage = (img) => {
    if (!img || img.trim() === "" || img === "null") return "/Margin.png";
    return img;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(
          `${BASE_URL}/predictions/history/${userId}`,
          { headers },
        );
        const data = res.data.data;
        setHistory(data);

        if (data.length > 0 && data[0].matches?.length > 0) {
          setUserName(data[0].user?.name || `User #${userId}`);
          setUserImage(data[0].user?.image || null);
        } else {
          setUserName(`User #${userId}`);
        }
      } catch (error) {
        console.error("Error fetching user history:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, token]);

  const allMatches = history.flatMap((round) => round.matches || []);

  if (loading)
    return <div className={styles.loader}>{t("common.loading")}</div>;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.profileBox}>
          <img
            src={getImage(userImage)}
            alt={userName}
            className={styles.avatar}
          />
          <div>
            <h1>{userName}</h1>
          </div>
        </div>
        <button className={styles.editBtn} onClick={() => navigate(-1)}>
          ← {t("prediction.backHome")}
        </button>
      </div>
      <div className={styles.section}>
        <h2 className={styles.title}>{t("prediction.myPredictions")}</h2>
        <p className={styles.subTitle}>{t("common.latestMatches")}</p>{" "}
        <div className={styles.activityList}>
          <div className={styles.headerRow}>
            <div></div>
            <div className={styles.rightHeader}>
              <span>{t("common.yourPrediction")}</span>
              <span>{t("common.result")}</span>
              <span>{t("common.points")}</span>
            </div>
          </div>

          {allMatches.length === 0 ? (
            <p style={{ textAlign: "center", opacity: 0.5, padding: "2rem" }}>
              {t("common.noPredictions")}{" "}
            </p>
          ) : (
            allMatches.map((item) => {
              const isWin = item.prediction.is_correct_result;
              const matchLabel = `${item.fixture.home_team.name} vs ${item.fixture.away_team.name}`;
              const predLabel = `${item.prediction.home_score}-${item.prediction.away_score}`;
              const resultLabel =
                item.fixture.status === "finished"
                  ? `${item.fixture.home_team.score}-${item.fixture.away_team.score}`
                  : "—";

              return (
                <div key={item.prediction_id} className={styles.activityCard}>
                  <div className={styles.left}>
                    <div
                      className={isWin ? styles.iconSuccess : styles.iconFail}
                    >
                      {isWin ? "✓" : "✕"}
                    </div>
                    <div>
                      <p className={styles.match}>{matchLabel}</p>
                      <span className={styles.date}>
                        {formatDate(item.fixture.match_date)}
                      </span>
                    </div>
                  </div>
                  <div className={styles.right}>
                    <div className={styles.mobileStat}>
                      <small>{t("common.yourPrediction")}</small>
                      <strong>{predLabel}</strong>
                    </div>

                    <div className={styles.mobileStat}>
                      <small>{t("common.result")}</small>
                      <strong>{resultLabel}</strong>
                    </div>

                    <div className={styles.mobileStat}>
                      <small>{t("common.points")}</small>

                      <span
                        className={isWin ? styles.pointsWin : styles.pointsLose}
                      >
                        {item.prediction.points > 0
                          ? `+${item.prediction.points}`
                          : "0"}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
